import { Flex } from "@radix-ui/themes";
import { useState } from "react";
import { hexToRgba } from "@/lib/utils";

export type FilterType = "all" | "SONG" | "BEAT" | "SOUNDKIT" | "ALBUM";

type FilterButtonProps = {
  label: string;
  isActive: boolean;
  onClick: () => void;
  primary: string;
  onPrimary: string;
  primaryInk: string;
  outline: string;
};

function FilterButton({
  label,
  isActive,
  onClick,
  primary,
  onPrimary,
  primaryInk,
  outline,
}: FilterButtonProps) {
  const [isHovered, setIsHovered] = useState(false);
  const isHoveredOrActive = isActive || isHovered;

  return (
    <button
      onClick={onClick}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
      className="flex h-[34px] px-4 justify-center items-center gap-1 rounded-full cursor-pointer transition-colors border bg-white"
      style={{
        fontFamily: "Open Sans",
        fontSize: "14px",
        fontWeight: 600,
        lineHeight: "140%",
        ...(isHoveredOrActive
          ? {
              backgroundColor: primary,
              color: onPrimary,
              borderColor: "transparent",
            }
          : {
              borderColor: hexToRgba(outline, 0.6),
              color: primaryInk,
            }),
      }}
    >
      {label}
    </button>
  );
}

type TrackFiltersProps = {
  activeFilter: FilterType;
  setActiveFilter: (filter: FilterType) => void;
  primary: string;
  onPrimary: string;
  primaryInk: string;
  outline: string;
  availableFilters: FilterType[];
  filterCounts?: Partial<Record<FilterType, number | string>>;
};

const filterLabels: Partial<Record<FilterType, string>> = {
  SONG: "Songs",
  BEAT: "Beats",
  SOUNDKIT: "Sound Kits",
  ALBUM: "Albums",
};

export function TrackFilters({
  activeFilter,
  setActiveFilter,
  primary,
  onPrimary,
  primaryInk,
  outline,
  availableFilters,
  filterCounts,
}: TrackFiltersProps) {
  // Only show filters that have content available
  const filters = availableFilters
    .map((value) => {
      const baseLabel = filterLabels[value];
      const count = filterCounts?.[value];
      const label =
        baseLabel && count != null ? `${baseLabel} (${count})` : baseLabel;
      return { value, label };
    })
    .filter((f) => f.label !== undefined) as Array<{
    value: FilterType;
    label: string;
  }>;

  return (
    <Flex className="gap-1 flex-wrap">
      {filters.map(({ value, label }) => (
        <FilterButton
          key={value}
          label={label}
          isActive={activeFilter === value}
          onClick={() => setActiveFilter(value)}
          primary={primary}
          onPrimary={onPrimary}
          primaryInk={primaryInk}
          outline={outline}
        />
      ))}
    </Flex>
  );
}
